import { createFileRoute, notFound } from "@tanstack/react-router";
import { GuidePageTemplate } from "@/components/GuidePageTemplate";
import { guideBySlug } from "@/data/guides";
import { articleBySlug } from "@/data/blog";
import { articleSchema, breadcrumbSchema, faqSchema, jsonLd, absUrl } from "@/lib/seo";

// Guides that duplicate a blog article consolidate onto the article (Google's
// recommended canonical-based consolidation). Slugs that differ only by a
// shortened form are mapped explicitly.
const CANONICAL_OVERRIDES: Record<string, string> = {
  "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar":
    "vad-kostar-en-bra-barberare-i-stockholm-och-vad-ingar-egentligen",
  "vad-ska-man-saga-till-barberaren-om-man-vill-ha-en-fade":
    "vad-ska-man-saga-till-barberaren-fade",
};

// Guides consolidated onto a service page instead of a blog article.
const SERVICE_CANONICALS: Record<string, string> = {
  "barbershop-skaggtrimning-stockholm": "/skaggtrimning",
};

export function canonicalTargetForGuide(slug: string): string {
  const service = SERVICE_CANONICALS[slug];
  if (service) return service;
  const target = CANONICAL_OVERRIDES[slug] ?? slug;
  return articleBySlug[target] ? `/${target}` : `/guider/${slug}`;
}

export const Route = createFileRoute("/guider/$slug")({
  head: ({ params }) => {
    const guide = params?.slug ? guideBySlug[params.slug] : undefined;
    if (!guide) return {};
    const canonicalPath = canonicalTargetForGuide(guide.slug);
    return {
      meta: [
        { title: guide.seoTitle },
        { name: "description", content: guide.metaDescription },
        { property: "og:title", content: guide.seoTitle },
        { property: "og:description", content: guide.metaDescription || guide.excerpt },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absUrl(canonicalPath) },
      ],
      links: [{ rel: "canonical", href: absUrl(canonicalPath) }],
      scripts: [
        jsonLd(articleSchema(guide)),
        ...(guide.faq?.length ? [jsonLd(faqSchema(guide.faq))] : []),
        jsonLd(breadcrumbSchema([
          { name: "Hem", url: "/" },
          { name: "Guider", url: "/guider" },
          { name: guide.title, url: `/guider/${guide.slug}` }
        ]))
      ]
    };
  },
  component: GuideSlugPage,
});

function GuideSlugPage() {
  const { slug } = Route.useParams();
  const guide = guideBySlug[slug];
  if (!guide) throw notFound();
  return <GuidePageTemplate guide={guide} />;
}
